import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  selectAuthLoading,
  selectIsAuthenticated
} from "../../features/auth/authSelectors";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Box,
  styled,
  Alert,
  InputAdornment,
  IconButton
} from "@mui/material";
import { getCurrentUser, loginUser } from "../../features/auth/authSlice";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const StyledTextFieldContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  margin: theme.spacing(4, 0)
}));

const StyledInputLabel = styled("label")(({ theme }) => ({
  position: "absolute",
  top: "-12px",
  color: "#333333",
  fontWeight: "bold",
  fontSize: "14px",
  pointerEvents: "none"
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      color: "#9CA3AF",
      borderColor: "#333333"
    },
    "&.Mui-focused fieldset": {
      borderColor: "#333333"
    }
  }
}));

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  const [formErrors, setFormErrors] = useState({
    email: false
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };
  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoading);
  const navigate = useNavigate();

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEmailValidation = (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      setFormErrors({ ...formErrors, email: !validateEmail(value) });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (Object.values(formErrors).some((error) => error)) {
      setSnackbarMessage("Please correct the form errors.");
      setOpenSnackbar(true);
      return;
    }
    const { payload } = await dispatch(loginUser(formData));
    if (payload.notActiveUser)
      navigate("/partners/signup", { state: { inActiveUser: payload?.user } });
    else if (!payload.error) navigate("/");
    else {
      setSnackbarMessage(payload.error || "Login failed.");
      setOpenSnackbar(true);
    }
  };

  const isAuthenticated = useSelector(selectIsAuthenticated);
  // const expirationTime = localStorage.getItem("expirationTime");
  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  if (isAuthenticated || localStorage.getItem("adminToken")) {
    return <Navigate to="/" />;
  }
  return (
    <Box
      display={"flex"}
      width="100%"
      overflow="hidden"
      sx={{
        flexDirection: { xs: "column", md: "row" }
      }}
    >
      <Box
        sx={{
          width: { xs: 0, md: "50%" },
          height: "100vh",
          objectFit: "cover",
          display: { xs: "none", md: "block" }
        }}
      >
        <Box
          component="img"
          src="/images/base.jpg"
          alt="base"
          sx={{
            width: { xs: 0, md: "100%" },
            height: "100vh",
            objectFit: "cover"
          }}
        />
      </Box>
      <Box
        sx={{
          left: 0,
          top: 0,
          width: { md: "0" },
          objectFit: "cover",
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: "20px",
          alignItems: "center",
          marginTop: "20px"
        }}
      >
        <Box
          component="img"
          src="/images/threepillars.png"
          alt="base"
          sx={{
            width: "100px",
            height: "100px",
            objectFit: "cover"
          }}
        />
      </Box>
      <Box
        flexGrow={1}
        paddingBottom={4}
        paddingLeft={10}
        sx={{
          paddingRight: { xs: 5, md: 13, lg: 30 },
          paddingLeft: { xs: 5, md: 10 },
          paddingTop: { xs: 7, md: 15, lg: 15 }
        }}
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          flexDirection="column"
          height="100%"
        >
          <Box>
            <Typography fontSize="30px" fontWeight="bold" gutterBottom>
              Welcome Back!
            </Typography>
            {/* <Typography color="#6B7280" fontSize="16px" gutterBottom>
              Sign in with email
            </Typography> */}
            <Snackbar
              open={openSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              autoHideDuration={1000}
              onClose={() => setOpenSnackbar(false)}
            >
              <Alert
                onClose={() => setOpenSnackbar(false)}
                severity="error"
                variant="filled"
                sx={{ width: "100%" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
            <Box component={"form"} mt={2} onSubmit={handleLogin}>
              <StyledTextFieldContainer>
                <StyledInputLabel htmlFor="email">
                  Email Address
                </StyledInputLabel>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  name="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onBlur={handleEmailValidation}
                  onChange={handleInputChange}
                  required
                  error={formErrors.email}
                  helperText={formErrors.email && "Enter a valid email address"}
                  margin="normal"
                />
              </StyledTextFieldContainer>
              <StyledTextFieldContainer>
                <StyledInputLabel htmlFor="password">Password</StyledInputLabel>
                <StyledTextField
                  fullWidth
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          onMouseUp={handleMouseUpPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  name="password"
                  placeholder="Enter Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  error={formErrors.password}
                  helperText={
                    formErrors.password &&
                    "Password must be 8 characters long: Contains 1 upper, 1 lower case, 1 symbol and 1 digit"
                  }
                  margin="normal"
                />
              </StyledTextFieldContainer>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Link
                  to="/forgot-password"
                  variant="outlined"
                  style={{
                    color: "#368AD0",
                    textDecoration: "none"
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || formErrors.email}
                sx={{
                  backgroundColor: "#1976d2",
                  cursor: "pointer",
                  color: "#fff",
                  width: "100%",
                  height: "42px",
                  ":disabled": {
                    backgroundColor: "#A7C8E9",
                    color: "#FFF",
                    pointerEvents: "none"
                  }
                }}
              >
                {loading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>
            </Box>
            <Box
              mt={3}
              display="flex"
              gap="20px"
              textAlign="center"
              justifyContent="center"
              alignItems="center"
            >
              <Link
                to="/partners"
                variant="outlined"
                style={{
                  color: "#1976d2",
                  textDecoration: "none"
                }}
              >
                Partners Register
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
